import { useAuth } from '@clerk/clerk-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
} from '@startupquickstart/frontend';
import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { ErrorSummaryCards } from './ErrorSummaryCards';
import { cn } from '@/lib/utils';

interface ValidationError {
  rowNumber: number;
  field: string;
  value: string;
  message: string;
  code: string;
}

interface ErrorSummary {
  byField: Record<string, number>;
  byCode: Record<string, number>;
}

interface PaginatedValidationErrorsProps {
  sessionId: string;
  /** Initial errors to display while fetching paginated data */
  initialErrors?: ValidationError[];
  /** Called when total error count changes */
  onTotalChange?: (total: number) => void;
}

const codeColors: Record<string, string> = {
  REQUIRED: 'bg-red-100 text-red-800',
  INVALID_TYPE: 'bg-orange-100 text-orange-800',
  INVALID_FORMAT: 'bg-yellow-100 text-yellow-800',
  FK_NOT_FOUND: 'bg-blue-100 text-blue-800',
  DUPLICATE: 'bg-purple-100 text-purple-800',
};

const PAGE_SIZE = 25;

/**
 * Paginated Validation Errors Component
 * Fetches and displays validation errors with pagination and filtering
 */
export function PaginatedValidationErrors({
  sessionId,
  initialErrors,
  onTotalChange,
}: PaginatedValidationErrorsProps) {
  const { getToken } = useAuth();
  const [errors, setErrors] = useState<ValidationError[]>(initialErrors || []);
  const [total, setTotal] = useState(initialErrors?.length || 0);
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [page, setPage] = useState(0);
  const [fieldFilter, setFieldFilter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchErrors = useCallback(async () => {
    if (!sessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });

      if (fieldFilter) {
        params.set('field', fieldFilter);
      }

      const response = await fetch(
        `/api/v1/import/sessions/${sessionId}/validation-errors?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch validation errors');
      }

      const result = await response.json();
      setErrors(result.data.errors);
      setTotal(result.data.total);
      setSummary(result.data.summary);
      onTotalChange?.(result.data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch errors');
      console.error('Error fetching validation errors:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, page, fieldFilter, getToken, onTotalChange]);

  // Fetch errors when sessionId, page, or filter changes
  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  // Reset to first page when filter changes
  const handleFieldFilterChange = (field: string | null) => {
    setFieldFilter(field);
    setPage(0);
  };

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm font-medium">{error}</span>
        </div>
      </div>
    );
  }

  if (errors.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Error Summary Cards */}
      {summary && (
        <ErrorSummaryCards
          summary={summary}
          total={total}
          activeFieldFilter={fieldFilter}
          onFieldFilterChange={handleFieldFilterChange}
        />
      )}

      {/* Error Table */}
      <div className="rounded-xl border overflow-hidden">
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow>
                <TableHead className="w-[80px]">Row</TableHead>
                <TableHead className="w-[120px]">Field</TableHead>
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead className="w-[150px]">Value</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {errors.map((err, index) => (
                <TableRow key={`${err.rowNumber}-${err.field}-${index}`}>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {err.rowNumber}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{err.field}</Badge>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        codeColors[err.code] || 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {err.code}
                    </span>
                  </TableCell>
                  <TableCell
                    className="font-mono text-sm truncate max-w-[150px]"
                    title={err.value}
                  >
                    {err.value || '<empty>'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {err.message}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="border-t bg-muted/30 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total} errors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show pages around current page
                  let pageNum = i;
                  if (totalPages > 5) {
                    if (page <= 2) {
                      pageNum = i;
                    } else if (page >= totalPages - 3) {
                      pageNum = totalPages - 5 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                      disabled={isLoading}
                    >
                      {pageNum + 1}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || isLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Please fix these errors in your CSV file and re-upload.
      </p>
    </div>
  );
}
